import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import moment from 'moment';
import styles from './style.module.scss';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import createGridCustomCells from '@components-clean/core/grid/custom-cell';
import { RowMenuAction } from '@components-clean/core/grid/grid.interface';
import Grid from '@components-clean/core/grid-fe';
import { DateCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import Loading from '@components-clean/core/loading/loading';
import { useReadOnlyContext } from '@contexts-clean/read-only-context';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { AddDataFilterDialog } from '@pages-clean/admin/study/admin-data-views/filters/data-filters/dialogs/add-data-filter-dialog';
import {
    ChipValues,
    DataFilter,
    DataFilterResultType,
    ExtraDataFields,
    FieldType,
} from '@pages-clean/admin/study/admin-data-views/filters/types';
import { datasourceLOV } from '@pages-clean/admin/study/admin-data-views/utils';
import { DataViewFilterValidation, RowDataSourceTable } from 'types/graphql-global-types';
import { IUser } from '@contexts/user-context';
import { useLocalState, useTranslation } from '@hooks/index';
import Constants, { RowDataSourceTableEnum } from '@utils/constants';
import NotificationService from '@utils/notification';
import {
    ADD_DATA_FILTER,
    ADD_SITE_AND_SUBJECT_DATA_FILTER,
    DELETE_SITE_AND_SUBJECT_FILTER,
    SAVE_SITE_AND_SUBJECT_DISPLAY_ORDER,
    UPDATE_DATA_FILTER,
    UPDATE_SITE_AND_SUBJECT_DATA_FILTER,
} from '@queries/central-monitoring';
import { DataViewFilterFragment } from '@queries/fragments';
import {
    addDataFilter,
    updateDataFilter,
    addSiteAndSubjectFilter,
    updateSiteAndSubjectFilter,
    deleteSiteAndSubjectFilter,
    updateSiteAndSubjectFilterDiplayOrder,
} from '@queries/generated/central-monitoring.generated';
import { StudyFields } from '@queries/generated/fragments.generated';

type DataFilterListProps = {
    user: IUser;
    study: StudyFields;
    filtersData: DataFilterResultType[];
    siteAndSubjectFilters: DataFilterResultType[];
    isLoading: boolean;
    canEdit: boolean;
    onDataFilterChange: (val: DataFilterResultType[]) => void;
    onSiteSubjectFilterChange: (val: DataFilterResultType[]) => void;
    selectedChip: ChipValues;
    extraDataFields?: ExtraDataFields;
};

type DataSourceLOVType = {
    key: string;
    value: RowDataSourceTableEnum;
    code: RowDataSourceTable;
    id: string;
    label: string;
};

const headerDatasource = datasourceLOV.map((x) => ({
    ...x,
    id: x.code,
    label: x.key,
}));
const siteDatasource: DataSourceLOVType = {
    key: 'Site',
    code: RowDataSourceTable.SITE,
    label: 'Site',
    id: RowDataSourceTable.SITE,
    value: RowDataSourceTableEnum.Site,
};
const subjectDatasource: DataSourceLOVType = {
    key: 'Subject',
    code: RowDataSourceTable.SUBJECT,
    label: 'Subject',
    id: RowDataSourceTable.SUBJECT,
    value: RowDataSourceTableEnum.Subject,
};
const nonRemovableFields = ['Site.name', 'Site.country', 'Site.mainPanelRAGStatus', 'Subject.subjectNumber'];

const COMPONENT_NAME = 'data-filter-list';

const DataFilterList = ({
    user,
    study,
    filtersData,
    siteAndSubjectFilters,
    isLoading,
    canEdit,
    onDataFilterChange,
    onSiteSubjectFilterChange,
    selectedChip,
    extraDataFields,
}: DataFilterListProps) => {
    const { t } = useTranslation();
    const { setReadOnly } = useReadOnlyContext();
    const [dataFilters, setDataFilters] = useState<DataFilter[]>([]);
    const [defaultSelectedItem, setDefaultSelectedItem] = useState<DataFilterResultType | undefined>();
    const { state } = useLocalState();
    const [selectedDataSource, setSelectedDataSource] = useState<DataSourceLOVType | null>(null);
    const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const [isReorderActive, setIsReorderActive] = useState<boolean>(false);
    const [selectedFilter, setSelectedFilter] = useState<DataFilter | null>(null);
    const isReviewer = user.systemAdminLevel === Constants.SystemAdminLevel.Reviewer;
    const isSysAdmin = user.systemAdminLevel === Constants.SystemAdminLevel.SysAdmin;
    const [saveDataFilterOrder] = useMutation<updateSiteAndSubjectFilterDiplayOrder>(
        SAVE_SITE_AND_SUBJECT_DISPLAY_ORDER,
    );
    const [deleteSiteSubjectFilter] = useMutation<deleteSiteAndSubjectFilter>(DELETE_SITE_AND_SUBJECT_FILTER);
    const { setComponentHasChanges, confirmDraggableChangesWillClearFilters } = useUnsavedChangesContext();

    const inActiveExtraDataFields: FieldType[] =
        extraDataFields?.extraDataFields
            ?.filter((item) => !item.active)
            ?.map((f) => ({
                name: f.friendlyName,
                key: `${Constants.DataSourceLOV.find((ds) => (ds.code as RowDataSourceTable) === f?.table)?.key.replace(
                    ' ',
                    '',
                )}.${f.sourceFieldName}`,
                dataType: f.table,
                active: false,
                status: 'Inactive',
            })) || [];

    const fieldDefinitionFields: FieldType[] =
        state?.fields
            ?.filter((field) => !field?.isHidden && !!field?.dataType)
            .flatMap((f) => {
                if (!f) return [];
                return [
                    {
                        name: f.name ?? '',
                        key: f.key ?? '',
                        dataType: f.dataType!,
                        active: true,
                        status: 'Active',
                    },
                ];
            }) || [];

    // Adding mainPanelRagStatus field in the existing fields list as we need this field in Site Filters only.
    fieldDefinitionFields?.push({
        key: t('admin:mainPanelRAGStatusFieldKey'),
        dataType: RowDataSourceTable.SITE,
        name: t('admin:mainPanelRAGStatusFieldName'),
        active: true,
        status: 'Active',
    });

    let gridTitle;
    let isRowReorderAllowed;
    let exportFileName;
    let isDeleteAllowed;
    switch (selectedChip) {
        case ChipValues.SiteFilters:
            gridTitle = t('admin:siteFilters');
            isRowReorderAllowed = true;
            exportFileName = t('admin:siteFilters');
            isDeleteAllowed = true;
            break;
        case ChipValues.SubjectFilters:
            gridTitle = t('admin:subjectFilters');
            isRowReorderAllowed = true;
            exportFileName = t('admin:subjectFilters');
            isDeleteAllowed = true;
            break;
        case ChipValues.DataFilters:
            gridTitle = headerDatasource;
            isRowReorderAllowed = false;
            exportFileName = `${t('admin:filters.dataFilters')}_${selectedDataSource?.key}`;
            isDeleteAllowed = false;
            break;
        default:
            gridTitle = t('admin:siteFilters');
    }

    const fields = [...fieldDefinitionFields, ...inActiveExtraDataFields];

    const mapItemToDataFilter = (item: DataFilterResultType | null): DataFilter => ({
        ...item,
        updatedByFullName: item?.updatedBy ? `${item.updatedBy.firstName} ${item.updatedBy.lastName}` : undefined,
        createdByFullName: item?.createdBy ? `${item?.createdBy?.firstName} ${item?.createdBy?.lastName}` : undefined,
        fieldKey: item?.fieldKey || '',
        fieldName: item?.fieldName ? item.fieldName.substring(item.fieldName.indexOf('.') + 1) : '',
        id: (item?.id as string) || '',
        label: item?.label || '',
        recordId: (item?.recordId as string) || '',
        configId: (item?.configId as string) || '',
        isDefault: item?.isDefault,
        created: item?.created,
        updated: item?.updated,
        displayIndex: item?.displayIndex || -1,
        isFieldActiveOrExists: fields?.find((f) => f.key === item?.fieldKey)?.active ?? false,
    });

    const defineOrder = (filters: DataFilter[]) =>
        filters.map((filter, index) => ({ ...filter, displayIndex: index + 1 }));

    const [updateFilter, { loading: isUpdating }] = useMutation<updateDataFilter>(UPDATE_DATA_FILTER, {
        update(cache, { data }) {
            if (
                !data?.updateDataFilter?.result ||
                data?.updateDataFilter?.validation !== DataViewFilterValidation.SUCCESS
            )
                return;

            cache.writeFragment({
                id: `DataViewFilter:${data.updateDataFilter.result.id}`,
                fragment: DataViewFilterFragment,
                data: data.updateDataFilter.result,
            });
        },
    });

    const [addFilter, { loading: isAdding }] = useMutation<addDataFilter>(ADD_DATA_FILTER, {
        update(cache, { data }) {
            if (!data?.addDataFilter?.result || data?.addDataFilter?.validation !== DataViewFilterValidation.SUCCESS)
                return;

            cache.writeFragment({
                id: `DataViewFilter:${data.addDataFilter.result.id}`,
                fragment: DataViewFilterFragment,
                data: data.addDataFilter.result,
            });
        },
    });

    const [addSiteAndSubject, { loading: isSiteAndSubjectAdding }] = useMutation<addSiteAndSubjectFilter>(
        ADD_SITE_AND_SUBJECT_DATA_FILTER,
        {
            update(cache, { data }) {
                if (
                    !data?.addSiteAndSubjectFilter?.result ||
                    data?.addSiteAndSubjectFilter?.validation !== DataViewFilterValidation.SUCCESS
                )
                    return;

                cache.writeFragment({
                    id: `SiteAndSubjectFilter:${data.addSiteAndSubjectFilter.result.id}`,
                    fragment: DataViewFilterFragment,
                    data: data.addSiteAndSubjectFilter.result,
                });
            },
        },
    );
    const [updateSiteAndSubject, { loading: isSiteAndSubjectUpdating }] = useMutation<updateSiteAndSubjectFilter>(
        UPDATE_SITE_AND_SUBJECT_DATA_FILTER,
        {
            update(cache, { data }) {
                if (
                    !data?.updateSiteAndSubjectFilter?.result ||
                    data?.updateSiteAndSubjectFilter?.validation !== DataViewFilterValidation.SUCCESS
                )
                    return;

                cache.writeFragment({
                    id: `SiteAndSubjectFilter:${data.updateSiteAndSubjectFilter.result.id}`,
                    fragment: DataViewFilterFragment,
                    data: data.updateSiteAndSubjectFilter.result,
                });
            },
        },
    );

    const saveRowOrder = (data: DataFilter[]) => {
        saveDataFilterOrder({
            variables: {
                studyId: study.id as string,
                filterIds: data.map((item) => item.id),
                dataSource: selectedDataSource?.code,
            },
        })
            .then(() => {
                NotificationService.showNotification({ text: t('actionsSuccessfulToast'), type: 'success' });
                onSiteSubjectFilterChange([
                    ...(defineOrder(data) as DataFilterResultType[]),
                    ...siteAndSubjectFilters.filter((item) => item.dataSource !== selectedDataSource?.code),
                ]);
            })
            .catch((err) => {
                window.console.error(err);
            });
    };

    const resetOrder = (deletedIndex: number, filters: DataFilter[]) =>
        filters.map((item) =>
            item.displayIndex > deletedIndex ? { ...item, displayIndex: item.displayIndex - 1 } : item,
        );

    const deleteFilter = (data: DataFilter) => {
        deleteSiteSubjectFilter({
            variables: {
                studyId: study.id as string,
                filterId: data.id,
                dataSource: selectedDataSource?.code,
            },
        })
            .then((resp) => {
                if (resp?.data?.deleteSiteAndSubjectFilter === DataViewFilterValidation.SUCCESS) {
                    NotificationService.showNotification({ text: t('actionsSuccessfulToast'), type: 'success' });
                } else {
                    window.console.error(resp?.data?.deleteSiteAndSubjectFilter);
                }

                onSiteSubjectFilterChange([
                    ...(resetOrder(
                        data.displayIndex,
                        siteAndSubjectFilters.filter(
                            (item) => item.id !== data.id && item.dataSource === selectedDataSource?.code,
                        ) as DataFilter[],
                    ) as DataFilterResultType[]),
                    ...siteAndSubjectFilters.filter((item) => item.dataSource !== selectedDataSource?.code),
                ]);

                setShowDeleteDialog(false);
            })
            .catch((err) => {
                window.console.error(err);
            });
    };

    const getFiltersForSource = (source: RowDataSourceTable, sort?: boolean) => {
        let data;
        if (source === RowDataSourceTable.SUBJECT || source === RowDataSourceTable.SITE) {
            data = siteAndSubjectFilters
                ?.filter((item) => item?.dataSource === source)
                .map((item) => mapItemToDataFilter(item))
                .sort((a, b) => a.displayIndex - b.displayIndex);
        } else {
            data = filtersData?.filter((item) => item?.dataSource === source).map((item) => mapItemToDataFilter(item));
            if (sort && data) data.sort((a, b) => a.label.localeCompare(b.label));
        }
        return data ?? [];
    };

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setSelectedDataSource(null);
    }, [study?.id]);

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        /* eslint-disable @eslint-react/set-state-in-effect */
        if (selectedChip === ChipValues.SubjectFilters) {
            setDataFilters(getFiltersForSource(RowDataSourceTable.SUBJECT));
            setSelectedDataSource(subjectDatasource);
        } else if (selectedChip === ChipValues.SiteFilters) {
            setDataFilters(getFiltersForSource(RowDataSourceTable.SITE));
            setSelectedDataSource(siteDatasource);
        } else if (selectedChip === ChipValues.DataFilters && selectedDataSource)
            setDataFilters(getFiltersForSource(selectedDataSource.code, true));
        else setDataFilters([]);
        /* eslint-enable @eslint-react/set-state-in-effect */
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [filtersData, siteAndSubjectFilters, extraDataFields]);

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, isDirty && isReorderActive);
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [isDirty, isReorderActive, showDeleteDialog]);

    const handleSubmitResponse = (resp?: DataFilterResultType | null) => {
        if (resp) {
            NotificationService.showNotification({ text: t('actionsSuccessfulToast'), type: 'success' });
            if (selectedChip === ChipValues.SubjectFilters || selectedChip === ChipValues.SiteFilters)
                if (siteAndSubjectFilters.find((item) => item.id === resp.id)) {
                    onSiteSubjectFilterChange(
                        siteAndSubjectFilters.map((item) => {
                            if (item.id === resp.id) return resp;
                            return item;
                        }),
                    );
                    setDataFilters(
                        dataFilters.map((item) => {
                            if (item.id === resp.id) return mapItemToDataFilter(resp);
                            return item;
                        }),
                    );
                } else {
                    onSiteSubjectFilterChange([...siteAndSubjectFilters, resp]);
                    setDataFilters([...dataFilters, mapItemToDataFilter(resp)]);
                }
            else if (selectedChip === ChipValues.DataFilters) {
                if (filtersData.find((item) => item.id === resp.id)) {
                    onDataFilterChange(
                        filtersData.map((item) => {
                            if (item.id === resp.id) return resp;
                            return item;
                        }),
                    );
                    setDataFilters(
                        dataFilters.map((item) => {
                            if (item.id === resp.id) return mapItemToDataFilter(resp);
                            return item;
                        }),
                    );
                } else {
                    onDataFilterChange([...filtersData, resp]);
                    setDataFilters(
                        [...dataFilters, mapItemToDataFilter(resp)].sort((a, b) => a.label.localeCompare(b.label)),
                    );
                }
            }
            setDefaultSelectedItem(resp);
        }
    };
    const { TooltipCell } = createGridCustomCells();

    const columns: GridColumnProps[] = [
        {
            title: t('label'),
            field: 'label',
            filter: 'text',
            minWidth: 170,
        },
        {
            title: t('field'),
            field: 'fieldName',
            filter: 'text',
            minWidth: 250,
            cells: {
                data: TooltipCell({
                    isCsv: false,
                    activeField: 'isFieldActiveOrExists',
                }),
            },
        },
        {
            title: t('metadataColumns.createdBy'),
            field: 'createdByFullName',
            filter: 'text',
            minWidth: 200,
        },
        {
            title: t('metadataColumns.createdDate'),
            field: 'created',
            filter: 'date',
            cells: {
                data: DateCell,
            },
            cellName: 'DateCell',
            minWidth: 120,
        },
        {
            title: t('metadataColumns.updatedBy'),
            field: 'updatedByFullName',
            filter: 'text',
            minWidth: 200,
        },
        {
            title: t('metadataColumns.updatedDate'),
            field: 'updated',
            filter: 'date',
            cells: {
                data: DateCell,
            },
            cellName: 'DateCell',
            minWidth: 120,
        },
        {
            title: t('configId'),
            field: 'configId',
            filter: 'text',
            minWidth: 150,
        },
        {
            title: t('recordId'),
            field: 'recordId',
            filter: 'numeric',
            minWidth: 105,
        },
    ];

    const showLoader = isLoading && (!filtersData || !siteAndSubjectFilters);
    const isMenuActionDisable =
        isReviewer || isAdding || isUpdating || isSiteAndSubjectAdding || isSiteAndSubjectUpdating;
    return (
        <div className="k-h-full">
            {showLoader && <Loading />}
            {!showLoader && (
                <Grid
                    showPlaceholder={false}
                    canEdit={
                        canEdit && !isUpdating && !isAdding && !isSiteAndSubjectAdding && !isSiteAndSubjectUpdating
                    }
                    title={gridTitle}
                    selectedTitle={selectedDataSource}
                    onTitleChange={(e) => {
                        if (e.value) {
                            const assertedValue = e.value as DataSourceLOVType;
                            setSelectedDataSource(assertedValue);
                            setDataFilters(getFiltersForSource(assertedValue.code, true));
                        }
                    }}
                    data={dataFilters}
                    defaultSelectedItem={defaultSelectedItem}
                    selectable={{ enabled: true, mode: 'single' }}
                    rowReorder={isRowReorderAllowed}
                    className="k-h-full"
                    maxHeight="auto"
                    testDataId="data-filters-grid"
                    columns={columns}
                    sortable
                    dataItemKey="recordId"
                    rowMenuActions={[
                        {
                            id: 1,
                            label: t('edit'),
                            onClick: (dataItem: unknown) => {
                                setSelectedFilter(dataItem as DataFilter);
                                setShowEditDialog(true);
                            },
                            icon: ['far', 'pen-to-square'],
                            disabled: isMenuActionDisable,
                        },
                        ...(isDeleteAllowed
                            ? [
                                  {
                                      id: 2,
                                      label: t('delete'),
                                      onClick: (dataItem: unknown) => {
                                          setSelectedFilter(dataItem as DataFilter);
                                          setShowDeleteDialog(true);
                                      },
                                      icon: ['fas', 'trash-xmark'],
                                      disabled: isMenuActionDisable || isSysAdmin,
                                      rowDisabled: (dataItem: unknown) => {
                                          const item = dataItem as DataFilter;
                                          return nonRemovableFields?.includes(item?.fieldKey);
                                      },
                                      colorProp: 'Red',
                                  } as RowMenuAction,
                              ]
                            : []),
                    ]}
                    // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
                    // eslint-disable-next-line @eslint-react/purity
                    exportFilename={`${exportFileName}_${study?.studyNumber}_${moment(new Date()).format(
                        'DD-MMM-yyyy',
                    )}`}
                    saveReorder={(data) => {
                        saveRowOrder(data as DataFilter[]);
                        setIsDirty(false);
                        setIsReorderActive(false);
                        return true;
                    }}
                    setReorderIsDirty={() => {
                        setIsDirty(true);
                    }}
                    setReorderIsActive={() => {
                        setIsReorderActive(true);
                    }}
                    cancelReorder={() => {
                        setIsDirty(false);
                        setIsReorderActive(false);
                        return true;
                    }}
                    reorderConfirmation={async (onSubmit, onClose) => {
                        setReadOnly(true);
                        const confirmed = await confirmDraggableChangesWillClearFilters(
                            t('admin:filters.editFilterOrderDialogTitle'),
                            t('admin:editGridRowOrderDialog'),
                            'data-filter-reorder-data-warning',
                        );
                        setReadOnly(false);
                        if (!confirmed) {
                            onClose();
                        } else {
                            onSubmit();
                        }
                    }}
                    addItemDialog={(onSubmit, onClose) => {
                        setShowEditDialog(false);
                        setSelectedFilter(null);
                        return (
                            <AddDataFilterDialog
                                onClose={onClose}
                                user={user}
                                existingFilters={dataFilters}
                                fields={fields}
                                selectedDataSource={selectedDataSource?.code}
                                type="add"
                                submit={(data) => {
                                    if (
                                        selectedChip === ChipValues.SubjectFilters ||
                                        selectedChip === ChipValues.SiteFilters
                                    ) {
                                        addSiteAndSubject({
                                            variables: {
                                                studyId: study?.id as string,
                                                label: data.label.trim(),
                                                fieldKey: data.field.key,
                                                fieldName: `${selectedDataSource?.label}.${data.field.name}`,
                                                dataSource: selectedDataSource?.code,
                                            },
                                        })
                                            .then((resp) => {
                                                onSubmit(true, false);
                                                handleSubmitResponse(resp.data?.addSiteAndSubjectFilter?.result);
                                            })
                                            .catch((err) => {
                                                window.console.error(err);
                                            });
                                    } else {
                                        addFilter({
                                            variables: {
                                                studyId: study?.id as string,
                                                label: data.label.trim(),
                                                fieldKey: data.field.key,
                                                fieldName: `${selectedDataSource?.label}.${data.field.name}`,
                                                dataSource: selectedDataSource?.code,
                                            },
                                        })
                                            .then((resp) => {
                                                onSubmit(true, false);
                                                handleSubmitResponse(resp?.data?.addDataFilter?.result);
                                            })
                                            .catch((err) => {
                                                window.console.error(err);
                                            });
                                    }
                                }}
                            />
                        );
                    }}
                />
            )}

            {showDeleteDialog && (
                <DraggableDialog
                    dataTestId="data-filter-delete--warning"
                    onSubmit={() => {
                        deleteFilter(selectedFilter as DataFilter);
                    }}
                    onClose={() => {
                        setShowDeleteDialog(false);
                    }}
                    title={`${t('admin:filters.deleteFilter')}: ${selectedFilter?.recordId}`}
                    initialWidth={500}
                    minHeight={193}
                    primaryBtnLabel={t('common:delete')}
                >
                    <div className="k-d-flex k-flex-column k-gap-2">
                        <div>{t('admin:filters.deleteFilterConfirmation')}</div>
                        <div>
                            <span className={styles.textBold}>{`${t('admin:filters.label')}`}:</span>{' '}
                            {selectedFilter?.label}
                        </div>
                        <div>
                            <span className={styles.textBold}>{`${t('admin:filters.field')}`}:</span>{' '}
                            {selectedFilter?.fieldName}
                        </div>
                    </div>
                </DraggableDialog>
            )}
            {showEditDialog && (
                <AddDataFilterDialog
                    existingFilters={dataFilters}
                    user={user}
                    fields={fields}
                    selectedDataSource={selectedDataSource?.code}
                    dataItem={selectedFilter as DataFilter}
                    type="edit"
                    nonRemovableFields={nonRemovableFields}
                    onClose={() => {
                        setShowEditDialog(false);
                    }}
                    submit={(data) => {
                        const filterToSave = {
                            configId: data.configId,
                            dataSource: selectedFilter?.dataSource,
                            id: selectedFilter?.id,
                            studyId: study?.id as string,
                            label: data.label.trim(),
                            fieldKey: data.field.key,
                            fieldName: `${selectedDataSource?.label}.${data.field.name}`,
                        };
                        if (selectedChip === ChipValues.SubjectFilters || selectedChip === ChipValues.SiteFilters) {
                            updateSiteAndSubject({
                                variables: { filter: filterToSave },
                            })
                                .then((resp) => {
                                    setShowEditDialog(false);
                                    handleSubmitResponse(resp.data?.updateSiteAndSubjectFilter?.result);
                                })
                                .catch((err) => {
                                    window.console.error(err);
                                });
                        } else {
                            updateFilter({
                                variables: { filter: filterToSave },
                            })
                                .then((resp) => {
                                    setShowEditDialog(false);
                                    handleSubmitResponse(resp.data?.updateDataFilter?.result);
                                })
                                .catch((err) => {
                                    window.console.error(err);
                                });
                        }
                    }}
                />
            )}
        </div>
    );
};
export default DataFilterList;
